import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import { isCompanyAdmin, setAuth, getToken } from '../authStorage';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamCode, setTeamCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [memTeamId, setMemTeamId] = useState('');
  const [memUsername, setMemUsername] = useState('');
  const [memRole, setMemRole] = useState('member');
  const [memSaving, setMemSaving] = useState(false);
  const [msgOk, setMsgOk] = useState('');
  const [allTeamsCatalog, setAllTeamsCatalog] = useState([]);
  const [linkTeamId, setLinkTeamId] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);
  const [orderSaving, setOrderSaving] = useState(false);

  const admin = isCompanyAdmin();

  const load = useCallback(async () => {
    setErr('');
    try {
      if (!isCompanyAdmin()) {
        try {
          const me = await api.get('/auth/me');
          if (me.data?.user) {
            const tok = getToken();
            if (tok) setAuth({ token: tok, user: me.data.user, teams: me.data.teams || [] });
          }
        } catch {
          /* ignore */
        }
      }
      const requests = [
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/teams`),
      ];
      if (isCompanyAdmin()) {
        requests.push(api.get('/teams'));
      }
      const results = await Promise.all(requests);
      const pr = results[0];
      const tm = results[1];
      setProject(pr.data);
      setTeams(tm.data || []);
      if (isCompanyAdmin() && results[2]) {
        setAllTeamsCatalog(results[2].data || []);
      } else {
        setAllTeamsCatalog([]);
      }
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Không tải được dự án');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  async function handleAddTeam(e) {
    e.preventDefault();
    const n = teamName.trim();
    if (!n) return;
    setSaving(true);
    try {
      await api.post(`/projects/${id}/teams`, {
        name: n,
        code: teamCode.trim() || null,
      });
      setTeamName('');
      setTeamCode('');
      const tm = await api.get(`/projects/${id}/teams`);
      setTeams(tm.data || []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Không tạo được đội');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddMember(e) {
    e.preventDefault();
    if (!memTeamId || !memUsername.trim()) return;
    setMemSaving(true);
    setErr('');
    try {
      await api.post(`/teams/${memTeamId}/members`, {
        username: memUsername.trim(),
        role: memRole,
      });
      setMemUsername('');
      setMsgOk('Đã gán thành viên vào đội.');
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Không gán được thành viên');
    } finally {
      setMemSaving(false);
    }
  }

  async function handleLinkExistingTeam(e) {
    e.preventDefault();
    if (!linkTeamId) return;
    setLinkSaving(true);
    setErr('');
    setMsgOk('');
    try {
      await api.post(`/projects/${id}/team-links`, { team_id: Number(linkTeamId) });
      setLinkTeamId('');
      setMsgOk('Đã gắn đội vào dự án này (có thể import / Gantt theo đội trên dự án).');
      const tm = await api.get(`/projects/${id}/teams`);
      setTeams(tm.data || []);
    } catch (er) {
      setErr(er.response?.data?.error || er.message || 'Không gắn được đội');
    } finally {
      setLinkSaving(false);
    }
  }

  async function persistTeamOrder(ordered) {
    setOrderSaving(true);
    setErr('');
    setMsgOk('');
    try {
      await api.put(`/projects/${id}/teams/order`, {
        ordered_team_ids: ordered.map((t) => t.id),
      });
      setTeams(ordered);
      setMsgOk('Đã cập nhật thứ tự hiển thị đội (Tiến độ, Gantt, danh sách).');
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Không lưu được thứ tự đội');
    } finally {
      setOrderSaving(false);
    }
  }

  function moveTeam(index, delta) {
    const j = index + delta;
    if (j < 0 || j >= teams.length) return;
    const next = [...teams];
    [next[index], next[j]] = [next[j], next[index]];
    void persistTeamOrder(next);
  }

  if (loading) return <p>Đang tải…</p>;
  if (err && !project) return <p style={{ color: '#b91c1c' }}>{err}</p>;
  if (!project) return null;

  return (
    <div>
      <h2>Chi tiết dự án</h2>
      {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
      {msgOk && <p style={{ color: '#15803d' }}>{msgOk}</p>}
      <div style={{ marginBottom: 16 }}>
        <b>Tên:</b> {project.name}
      </div>
      {project.description ? (
        <div style={{ marginBottom: 16 }}>
          <b>Mô tả:</b> {project.description}
        </div>
      ) : null}
      <p style={{ marginBottom: 20 }}>
        <Link to="/projects">← Danh sách dự án</Link>
        {' · '}
        <Link to={`/gantt?project=${id}`}>Biểu đồ Gantt (cả dự án)</Link>
      </p>

      <h3 style={{ marginTop: 28 }}>Đội thi công</h3>
      <p style={{ fontSize: 14, color: '#64748b', maxWidth: 640 }}>
        <strong>Mỗi tài khoản nhà thầu đã được admin chấp nhận</strong> có một <strong>đội riêng trên từng dự án</strong>{' '}
        (tên theo email), tự làm lead — import Excel và % tiến độ trên{' '}
        <Link to="/progress">Tiến độ</Link>. <strong>Cùng một đội tạo sẵn</strong> (ví dụ đội nhóm do quản trị tạo){' '}
        có thể được <strong>gắn thêm vào dự án khác</strong> để làm một phần công việc trên nhiều dự án; thành viên và
        vai trò giữ nguyên. Quản trị có thể tạo đội mới hoặc gắn đội có sẵn phía dưới. Hàng import: chỉnh sửa chi tiết
        (tên, ngày…) do quản trị thực hiện.
      </p>

      {admin ? (
        <form
          onSubmit={handleAddTeam}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end',
            marginTop: 12,
            marginBottom: 20,
            padding: 14,
            background: '#f8fafc',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13 }}>Tên đội mới</span>
            <input
              value={teamName}
              onChange={(ev) => setTeamName(ev.target.value)}
              placeholder="Ví dụ: Đội kết cấu"
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 200 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13 }}>Mã đội (tuỳ chọn)</span>
            <input
              value={teamCode}
              onChange={(ev) => setTeamCode(ev.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', width: 120 }}
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              background: saving ? '#94a3b8' : '#059669',
              color: '#fff',
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            Thêm đội
          </button>
        </form>
      ) : (
        <p style={{ fontSize: 14, color: '#92400e' }}>Chỉ tài khoản quản trị mới tạo đội mới trên dự án.</p>
      )}

      {admin && allTeamsCatalog.length > 0 && (
        <form
          onSubmit={handleLinkExistingTeam}
          style={{
            marginBottom: 20,
            padding: 14,
            background: '#f0fdf4',
            borderRadius: 10,
            border: '1px solid #86efac',
          }}
        >
          <h4 style={{ marginTop: 0, marginBottom: 8 }}>Gắn đội có sẵn vào dự án này</h4>
          <p style={{ fontSize: 13, color: '#166534', marginTop: 0, marginBottom: 12 }}>
            Chọn đội đã tồn tại (có thể tạo trên dự án khác). Sau khi gắn, đội xuất hiện trong danh sách dưới đây và
            có thể import / xem Gantt trên <strong>dự án hiện tại</strong>.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13 }}>Đội</span>
              <select
                value={linkTeamId}
                onChange={(ev) => setLinkTeamId(ev.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 240 }}
              >
                <option value="">— Chọn đội chưa có trên dự án này —</option>
                {allTeamsCatalog
                  .filter((t) => !teams.some((ot) => Number(ot.id) === Number(t.id)))
                  .map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      #{t.id} · {t.name}
                      {t.project_id && Number(t.project_id) !== Number(id) ? ` (tạo tại dự án #${t.project_id})` : ''}
                    </option>
                  ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={linkSaving || !linkTeamId}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                background: linkSaving || !linkTeamId ? '#94a3b8' : '#16a34a',
                color: '#fff',
                cursor: linkSaving ? 'wait' : 'pointer',
              }}
            >
              Gắn vào dự án
            </button>
          </div>
        </form>
      )}

      {admin && teams.length > 0 && (
        <>
          <p style={{ marginBottom: 12, fontSize: 13, color: '#64748b' }}>
            User phải tồn tại: quản trị có thể tạo tài khoản qua <code>POST /api/users</code> hoặc SQL; sau đó gán vào đội tại đây.
          </p>
          <form
            onSubmit={handleAddMember}
            style={{
              marginBottom: 24,
              padding: 14,
              background: '#eff6ff',
              borderRadius: 10,
              border: '1px solid #bfdbfe',
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: 10 }}>Gán user vào đội</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13 }}>Đội</span>
              <select
                value={memTeamId}
                onChange={(ev) => setMemTeamId(ev.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 200 }}
              >
                <option value="">— Chọn —</option>
                {teams.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13 }}>Username</span>
              <input
                value={memUsername}
                onChange={(ev) => setMemUsername(ev.target.value)}
                placeholder="Email đăng nhập (đã duyệt)"
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 180 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13 }}>Vai trò</span>
              <select
                value={memRole}
                onChange={(ev) => setMemRole(ev.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1' }}
              >
                <option value="lead">lead (import, %; sửa/xóa chỉ hàng không Excel)</option>
                <option value="member">member (import, sửa %)</option>
                <option value="viewer">viewer (chỉ xem)</option>
              </select>
            </label>
            <button
              type="submit"
              disabled={memSaving}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                background: memSaving ? '#94a3b8' : '#2563eb',
                color: '#fff',
                cursor: memSaving ? 'wait' : 'pointer',
              }}
            >
              Gán
            </button>
          </div>
        </form>
        </>
      )}

      {admin && teams.length > 1 ? (
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
          <strong>Thứ tự đội:</strong> dùng nút lên / xuống để sắp xếp — áp dụng cho dropdown chọn đội trên trang Tiến độ, Gantt và khi xem &quot;Tất cả đội&quot;.
        </p>
      ) : null}

      <table border="1" cellPadding={8} style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: '#f1f5f9' }}>
          <tr>
            {admin && teams.length > 1 ? (
              <th style={{ width: 120, whiteSpace: 'nowrap' }}>Thứ tự</th>
            ) : null}
            <th align="left">Đội</th>
            <th>Mã</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {teams.map((t, idx) => (
            <tr key={t.id}>
              {admin && teams.length > 1 ? (
                <td align="center">
                  <span style={{ marginRight: 6, fontWeight: 600 }}>{idx + 1}</span>
                  <button
                    type="button"
                    disabled={orderSaving || idx === 0}
                    onClick={() => moveTeam(idx, -1)}
                    title="Lên"
                    style={{
                      marginRight: 4,
                      padding: '2px 8px',
                      cursor: orderSaving || idx === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    disabled={orderSaving || idx === teams.length - 1}
                    onClick={() => moveTeam(idx, 1)}
                    title="Xuống"
                    style={{
                      padding: '2px 8px',
                      cursor: orderSaving || idx === teams.length - 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    ↓
                  </button>
                </td>
              ) : null}
              <td>{t.name}</td>
              <td>{t.code || '—'}</td>
              <td>
                <Link to={`/progress?project=${id}&team=${t.id}`}>Tiến độ / import</Link>
                {' · '}
                <Link to={`/gantt?project=${id}&team=${t.id}`}>Gantt đội</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {teams.length === 0 && (
        <p style={{ marginTop: 12 }}>
          {admin
            ? 'Chưa có đội — quản trị thêm đội phía trên.'
            : 'Trên dự án này bạn chưa có đội — thử đăng xuất và đăng nhập lại; tài khoản đã duyệt sẽ được tạo đội riêng tự động. Nếu vẫn trống, liên hệ quản trị.'}
        </p>
      )}
    </div>
  );
};

export default ProjectDetailPage;
